import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Textarea } from '../../../components/ui/Textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/Card';
import { useAuth } from '../../../core/contexts/AuthContext';
import { useCurrentOrganization } from '../../../core/hooks/useCurrentOrganization';
import { storage, db } from '../../../core/services/firebase';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { doc, updateDoc } from 'firebase/firestore';
import type { OrganizationSettings } from '../../../core/models/Organization';

interface GeneralSettingsFormValues {
  companyName: string;
  companyEmail: string;
  companyPhone: string;
  companyAddress: string;
  defaultCurrency: string;
  defaultTaxRate: number;
}

interface GeneralSettingsProps {
  organizationIdOverride?: string | null;
}

export const GeneralSettings: React.FC<GeneralSettingsProps> = ({ organizationIdOverride }) => {
  const { organizationId: authOrganizationId } = useAuth();
  const effectiveOrganizationId = organizationIdOverride ?? authOrganizationId;
  const { organization, loading } = useCurrentOrganization(effectiveOrganizationId);
  const [saving, setSaving] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoError, setLogoError] = useState<string | null>(null);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { isSubmitting },
    reset,
  } = useForm<GeneralSettingsFormValues>({
    defaultValues: {
      companyName: '',
      companyEmail: '',
      companyPhone: '',
      companyAddress: '',
      defaultCurrency: 'USD',
      defaultTaxRate: 10,
    },
  });

  useEffect(() => {
    if (!organization) return;

    const settings: OrganizationSettings | undefined = organization.settings;
    const addressLines = [
      organization.address?.street,
      [organization.address?.city, organization.address?.state, organization.address?.postalCode]
        .filter(Boolean)
        .join(', '),
      organization.address?.country,
    ]
      .filter(Boolean)
      .join('\n');

    reset({
      companyName: organization.name || '',
      companyEmail: organization.email || '',
      companyPhone: organization.phone || '',
      companyAddress: addressLines,
      defaultCurrency: settings?.defaultCurrency || 'USD',
      defaultTaxRate: settings?.taxRate ?? 10,
    });

    if (organization.branding?.logoUrl) {
      setLogoPreviewUrl(organization.branding.logoUrl);
    } else {
      setLogoPreviewUrl(null);
    }
  }, [organization, reset]);

  const onSubmit = async (data: GeneralSettingsFormValues) => {
    if (!effectiveOrganizationId) {
      alert('Client is not set for this user, so settings cannot be saved.');
      return;
    }
    if (!organization) {
      alert('Client details are still loading. Please try again in a moment.');
      return;
    }

    try {
      setSaving(true);
      const { companyName, companyEmail, companyPhone, companyAddress, defaultCurrency, defaultTaxRate } = data;

      const updates: Partial<OrganizationSettings> & {
        name?: string;
        email?: string;
        phone?: string;
      } = {
        name: companyName,
        email: companyEmail,
        phone: companyPhone,
        defaultCurrency,
        taxRate: defaultTaxRate,
      };

      // Parse the freeform address textarea back into the structured address object
      const existingAddress = organization.address || ({} as any);
      const addressLines = companyAddress
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean);

      const [line1, line2, line3] = addressLines;

      // Start from existing values so we don't accidentally blank fields
      const street = line1 ?? existingAddress.street ?? '';
      let city = existingAddress.city ?? '';
      let state = existingAddress.state ?? '';
      let postalCode = existingAddress.postalCode ?? '';
      let country = existingAddress.country ?? '';

      if (line2) {
        // Handle both "City, State, Postal" (our display format)
        // and "City, ST 12345" (manual entry).
        const parts = line2.split(',').map((part) => part.trim()).filter(Boolean);
        const [cityPart, ...restParts] = parts;

        if (cityPart) {
          city = cityPart;
        }

        const restJoined = restParts.join(', ').trim();
        if (restJoined) {
          // If there's still a comma, assume "State, Postal"
          if (restJoined.includes(',')) {
            const statePostalParts = restJoined
              .split(',')
              .map((p) => p.trim())
              .filter(Boolean);
            if (statePostalParts.length >= 1) {
              state = statePostalParts[0];
            }
            if (statePostalParts.length >= 2) {
              postalCode = statePostalParts.slice(1).join(', ');
            }
          } else {
            // Format like "ST 12345"
            const tokens = restJoined.split(/\s+/).filter(Boolean);
            if (tokens.length > 0) {
              state = tokens[0];
              if (tokens.length > 1) {
                postalCode = tokens.slice(1).join(' ');
              }
            }
          }
        }
      }

      if (line3) {
        country = line3;
      }

      const updatedAddress = {
        street,
        city,
        state,
        postalCode,
        country,
      };

      const orgRef = doc(db, 'clients', effectiveOrganizationId);

      await updateDoc(orgRef, {
        entityType: 'client',
        name: updates.name,
        email: updates.email,
        phone: updates.phone,
        address: updatedAddress,
        settings: {
          ...(organization.settings || {}),
          defaultCurrency: updates.defaultCurrency,
          taxRate: updates.taxRate,
        },
      });
    } catch (err) {
      console.error('Saving general settings failed:', err);
      alert('Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleLogoChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files?.[0]) return;
    if (!effectiveOrganizationId) {
      alert('Client is not set for this user, so logo cannot be uploaded.');
      return;
    }

    const file = event.target.files[0];
    const maxSizeBytes = 2 * 1024 * 1024; // 2 MB

    if (!['image/png', 'image/jpeg', 'image/svg+xml'].includes(file.type)) {
      setLogoError('Please upload a PNG, JPG, or SVG file.');
      return;
    }

    if (file.size > maxSizeBytes) {
      setLogoError('Logo must be smaller than 2 MB.');
      return;
    }

    setLogoError(null);
    setLogoUploading(true);

    try {
      const extension = file.name.split('.').pop() || 'png';
      const storagePath = `clients/${effectiveOrganizationId}/branding/logo-${Date.now()}.${extension}`;
      const storageRef = ref(storage, storagePath);

      await uploadBytes(storageRef, file);
      const downloadUrl = await getDownloadURL(storageRef);

      const orgRef = doc(db, 'clients', effectiveOrganizationId);

      await updateDoc(orgRef, {
        entityType: 'client',
        branding: {
          ...(organization?.branding || {}),
          logoUrl: downloadUrl,
          logoFileName: file.name,
          logoUpdatedAt: new Date(),
        },
      });

      setLogoPreviewUrl(downloadUrl);
    } catch (err) {
      console.error('Uploading logo failed:', err);
      setLogoError('Failed to upload logo. Please try again.');
    } finally {
      setLogoUploading(false);
    }
  };

  const handleRemoveLogo = async () => {
    if (!effectiveOrganizationId) {
      alert('Client is not set for this user, so logo cannot be removed.');
      return;
    }
    if (!organization?.branding?.logoUrl) return;

    setLogoError(null);
    setLogoUploading(true);

    try {
      const orgRef = doc(db, 'clients', effectiveOrganizationId);

      await updateDoc(orgRef, {
        entityType: 'client',
        branding: {
          ...(organization.branding || {}),
          logoUrl: null,
          logoFileName: null,
          logoUpdatedAt: null,
        },
      });

      setLogoPreviewUrl(null);
    } catch (err) {
      console.error('Removing logo failed:', err);
      setLogoError('Failed to remove logo. Please try again.');
    } finally {
      setLogoUploading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>General Settings</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <Input label="Company Name" {...register('companyName')} disabled={loading || saving} />
          <Input
            label="Company Email"
            type="email"
            {...register('companyEmail')}
            disabled={loading || saving}
          />
          <Input label="Company Phone" {...register('companyPhone')} disabled={loading || saving} />
          <Textarea
            label="Company Address"
            {...register('companyAddress')}
            rows={3}
            disabled={loading || saving}
          />
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Input
              label="Default Currency"
              {...register('defaultCurrency')}
              disabled={loading || saving}
            />
            <Input
              label="Default Tax Rate (%)"
              type="number"
              {...register('defaultTaxRate', { valueAsNumber: true })}
              disabled={loading || saving}
            />
          </div>
          <Button type="submit" loading={isSubmitting || saving} disabled={loading || saving}>
            Save Settings
          </Button>
        </form>

        <div className="border-t pt-4 space-y-4">
          <h3 className="font-semibold text-base">Brand Logo</h3>
          <p className="text-sm text-muted-foreground">
            Upload your client&apos;s logo to use as the default branding on documents.
          </p>

          <div className="flex items-start gap-4">
            <div className="w-32 h-32 border rounded-md bg-muted flex items-center justify-center overflow-hidden">
              {logoPreviewUrl ? (
                <img src={logoPreviewUrl} alt="Client logo" className="max-w-full max-h-full object-contain" />
              ) : (
                <span className="text-xs text-muted-foreground text-center px-2">
                  No logo uploaded
                </span>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <Input
                type="file"
                accept="image/png,image/jpeg,image/svg+xml"
                onChange={handleLogoChange}
                disabled={logoUploading || loading}
              />
              <p className="text-xs text-muted-foreground">
                Recommended: square logo, PNG or SVG, up to 2 MB.
              </p>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleRemoveLogo}
                  disabled={logoUploading || loading || !logoPreviewUrl}
                >
                  Remove Logo
                </Button>
              </div>
              {logoError && <p className="text-xs text-destructive">{logoError}</p>}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
